import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Search, FileText, Book, Archive, Ticket, Scale, ExternalLink, 
  Filter, X, ChevronDown, ChevronUp, Eye 
} from "lucide-react";
import { api } from "@/lib/api";

const TYPE_CONFIG = {
  claims: { 
    label: "Claims", 
    icon: FileText, 
    color: "bg-blue-100 text-blue-700",
    singular: "claim"
  },
  facts: { 
    label: "Fact Corrections", 
    icon: Scale, 
    color: "bg-red-100 text-red-700",
    singular: "correction"
  },
  research: { 
    label: "Research", 
    icon: Book, 
    color: "bg-purple-100 text-purple-700",
    singular: "document"
  },
  artifacts: { 
    label: "Artifacts", 
    icon: Archive, 
    color: "bg-amber-100 text-amber-700",
    singular: "artifact"
  },
  tickets: { 
    label: "Tickets", 
    icon: Ticket, 
    color: "bg-green-100 text-green-700",
    singular: "entry"
  }
};

const CLAIM_STATUSES = ["Documented", "Disputed", "Unverified"];
const VERIFICATION_STATUSES = ["Verified", "Partially Verified", "Unverified", "Disputed"];
const CONFIDENCE_LEVELS = ["High", "Medium", "Low"];
const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "updated", label: "Recently Updated" }
];

const StatusBadge = ({ type, value }) => {
  if (!value) return null;
  
  const styles = {
    Documented: "bg-green-100 text-green-700",
    Disputed: "bg-red-100 text-red-700",
    Unverified: "bg-yellow-100 text-yellow-700",
    "Partially Verified": "bg-yellow-100 text-yellow-700",
    Verified: "bg-green-100 text-green-700",
    High: "bg-green-100 text-green-700",
    Medium: "bg-yellow-100 text-yellow-700",
    Low: "bg-red-100 text-red-700"
  };
  
  return (
    <Badge className={`text-xs ${styles[value] || "bg-gray-100 text-gray-700"}`}>
      {value}
    </Badge>
  );
};

const SearchResultItem = ({ item, query }) => {
  const typeKey = item.type === "fact_correction" ? "facts" : item.type;
  const config = TYPE_CONFIG[typeKey] || TYPE_CONFIG.claims;
  const Icon = config.icon;
  
  // Snippet may already have <mark> tags from backend
  const hasBackendHighlight = item.snippet?.includes("<mark>");
  
  return (
    <Link 
      to={item.url}
      className="block p-4 hover:bg-gray-50 border-b last:border-b-0 transition-colors"
      data-testid={`search-result-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded flex-shrink-0 ${config.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 
              className="font-medium text-gray-900 line-clamp-2"
              dangerouslySetInnerHTML={{ 
                __html: hasBackendHighlight ? item.title : item.title 
              }}
            />
            <span className="text-xs text-gray-400 flex-shrink-0">
              {item.score?.toFixed(1)}
            </span>
          </div>
          
          <p 
            className="text-sm text-gray-600 mt-1 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: item.snippet || "" }}
          />
          
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="text-xs">{config.label}</Badge>
            
            {/* Status badges */}
            {item.status && <StatusBadge value={item.status} />}
            {item.verification_status && <StatusBadge value={item.verification_status} />}
            {item.confidence_level && <StatusBadge value={item.confidence_level} />}
            {item.category && (
              <Badge variant="secondary" className="text-xs">{item.category}</Badge>
            )}
            {item.section && (
              <Badge variant="secondary" className="text-xs">{item.section}</Badge>
            )}
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
      </div>
    </Link>
  );
};

const SearchResultGroup = ({ type, items, query, collapsed, onToggle }) => {
  const config = TYPE_CONFIG[type];
  if (!config || items.length === 0) return null;
  
  const Icon = config.icon;
  const displayItems = collapsed ? items.slice(0, 3) : items;
  
  return (
    <div className="mb-6" data-testid={`search-group-${type}`}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 mb-3 w-full text-left group"
      >
        <Icon className="w-5 h-5 text-gray-600" />
        <h2 className="text-lg font-semibold">{config.label}</h2>
        <Badge variant="secondary">{items.length}</Badge>
        <span className="flex-1" />
        {items.length > 3 && (
          collapsed ? (
            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          )
        )}
      </button>
      <div className="bg-white rounded-lg border overflow-hidden">
        {displayItems.map(item => (
          <SearchResultItem key={item.id} item={item} query={query} />
        ))}
        {collapsed && items.length > 3 && (
          <button
            onClick={onToggle}
            className="w-full p-3 text-sm text-blue-600 hover:bg-blue-50 text-center"
          >
            Show {items.length - 3} more {config.label.toLowerCase()}
          </button>
        )}
      </div>
    </div>
  );
};

const FilterSidebar = ({ 
  enabledTypes, 
  setEnabledTypes, 
  filters, 
  setFilters,
  counts 
}) => {
  const [showFilters, setShowFilters] = useState(true);
  
  const handleTypeToggle = (type, checked) => {
    if (checked) {
      setEnabledTypes(prev => [...prev, type]);
    } else {
      setEnabledTypes(prev => prev.filter(t => t !== type));
    }
  };
  
  const clearFilters = () => {
    setFilters({});
    setEnabledTypes(Object.keys(TYPE_CONFIG));
  };
  
  const hasActiveFilters = Object.values(filters).some(v => v) || 
    enabledTypes.length < Object.keys(TYPE_CONFIG).length;
  
  return (
    <aside className="w-64 flex-shrink-0 bg-white border rounded-lg p-4 h-fit sticky top-20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filters
        </h3>
        {hasActiveFilters && (
          <button 
            onClick={clearFilters}
            className="text-xs text-blue-600 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>
      
      {/* Entity Type Toggles */}
      <div className="mb-6">
        <Label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
          Entity Types
        </Label>
        <div className="space-y-2">
          {Object.entries(TYPE_CONFIG).map(([key, config]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={enabledTypes.includes(key)}
                onCheckedChange={(checked) => handleTypeToggle(key, checked)}
              />
              <span className="text-sm">{config.label}</span>
              {counts[key] > 0 && (
                <span className="text-xs text-gray-400 ml-auto">{counts[key]}</span>
              )}
            </label>
          ))}
        </div>
      </div>
      
      {/* Status Filters */}
      <div className="space-y-4">
        {/* Claim Status */}
        {enabledTypes.includes("claims") && (
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Claim Status
            </Label>
            <Select 
              value={filters.claim_status || "all"} 
              onValueChange={(v) => setFilters(f => ({ ...f, claim_status: v === "all" ? "" : v }))}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {CLAIM_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Artifact Verification */}
        {enabledTypes.includes("artifacts") && (
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Artifact Verification
            </Label>
            <Select 
              value={filters.verification_status || "all"} 
              onValueChange={(v) => setFilters(f => ({ ...f, verification_status: v === "all" ? "" : v }))}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {VERIFICATION_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Fact Confidence */}
        {enabledTypes.includes("facts") && (
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Confidence Level
            </Label>
            <Select 
              value={filters.confidence_level || "all"} 
              onValueChange={(v) => setFilters(f => ({ ...f, confidence_level: v === "all" ? "" : v }))}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {CONFIDENCE_LEVELS.map(l => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Sort */}
        <div>
          <Label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
            Sort By
          </Label>
          <Select 
            value={filters.sort || "relevance"} 
            onValueChange={(v) => setFilters(f => ({ ...f, sort: v }))}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </aside>
  );
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(queryFromUrl);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enabledTypes, setEnabledTypes] = useState(Object.keys(TYPE_CONFIG));
  const [filters, setFilters] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append("q", query);
      
      if (enabledTypes.length < Object.keys(TYPE_CONFIG).length) {
        params.append("types", enabledTypes.join(","));
      }
      
      if (filters.claim_status) params.append("claim_status", filters.claim_status);
      if (filters.verification_status) params.append("verification_status", filters.verification_status);
      if (filters.confidence_level) params.append("confidence_level", filters.confidence_level);
      if (filters.sort) params.append("sort", filters.sort);
      
      const res = await api.get(`/search?${params.toString()}`);
      setResults(res.data);
    } catch (err) {
      setError("Failed to perform search. Please try again.");
      setResults(null);
    }
    
    setLoading(false);
  }, [enabledTypes, filters]);

  useEffect(() => {
    if (queryFromUrl) {
      setInputValue(queryFromUrl);
      performSearch(queryFromUrl);
    }
  }, [queryFromUrl, performSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (inputValue.trim().length >= 2) {
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const toggleGroup = (type) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Order results by priority: claims, facts, research, artifacts, tickets
  const orderedTypes = ["claims", "facts", "research", "artifacts", "tickets"];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="search-page">
      {/* Header with search */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search across all archive content..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="pl-10 h-12 text-lg"
                data-testid="search-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary px-6"
              disabled={loading || inputValue.trim().length < 2}
              data-testid="search-submit"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </form>
        </div>
      </div>

      {/* Main content with sidebar */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Filter Sidebar - hidden on mobile, shown on desktop */}
          {queryFromUrl && (
            <div className="hidden md:block">
              <FilterSidebar
                enabledTypes={enabledTypes}
                setEnabledTypes={setEnabledTypes}
                filters={filters}
                setFilters={setFilters}
                counts={results?.counts || {}}
              />
            </div>
          )}
          
          {/* Results */}
          <div className="flex-1 min-w-0">
            {/* Loading state */}
            {loading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            )}

            {/* No query */}
            {!queryFromUrl && !loading && (
              <div className="text-center py-16">
                <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h2 className="text-xl font-medium text-gray-600 mb-2">Search the Archive</h2>
                <p className="text-gray-500">
                  Enter at least 2 characters to search across claims, research, artifacts, tickets, and fact corrections.
                </p>
              </div>
            )}

            {/* Results */}
            {results && !loading && (
              <>
                {/* Summary */}
                <div className="mb-6">
                  <p className="text-gray-600">
                    Found <strong>{results.total}</strong> result{results.total !== 1 ? "s" : ""} for "{results.query}"
                  </p>
                  {results.filters && Object.keys(results.filters).length > 0 && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <span>Active filters:</span>
                      {Object.entries(results.filters).map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {key}: {value}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Result groups */}
                {results.total === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No results found. Try different keywords or adjust filters.</p>
                  </div>
                ) : (
                  orderedTypes.map(type => (
                    <SearchResultGroup 
                      key={type} 
                      type={type} 
                      items={results.results[type] || []} 
                      query={results.query}
                      collapsed={collapsedGroups[type] !== false && (results.results[type]?.length || 0) > 5}
                      onToggle={() => toggleGroup(type)}
                    />
                  ))
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
